/**
 * Background Jobs Dropdown
 * Shows import/export job status in header
 * Fetches jobs on click (not polling) to reduce API calls
 */

import React, { useState, useCallback } from 'react';
import { ClipboardList, CheckCircle, XCircle, Loader2, Clock, RefreshCw, FileSpreadsheet, Download, Upload } from 'lucide-react';
import SimpleBar from 'simplebar-react';
import { Dropdown } from './Dropdown';
import { userService } from '../../../modules/master-data/User/services';
import { formatDateForLogs as formatDate } from '../../utils/formatters';
import logger from '../../utils/logger';

interface BackgroundJob {
  jobId: string;
  jobName: string;
  type: 'import' | 'export';
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  totalRows?: number;
  successCount?: number;
  failedCount?: number;
  createdAt: string;
  completedAt?: string;
}

const BackgroundJobsDropdown: React.FC = () => {
  const [jobs, setJobs] = useState<BackgroundJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await userService.getJobHistory({ limit: 10 });
      if (response?.jobs) {
        setJobs(response.jobs);
        setHasFetched(true);
      }
    } catch (error) {
      logger.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on dropdown open (first click)
  const handleDropdownOpen = useCallback(() => {
    fetchJobs();
  }, [fetchJobs]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'failed':
        return <XCircle size={16} className="text-red-500" />;
      case 'processing':
        return <Loader2 size={16} className="text-blue-500 animate-spin" />;
      case 'pending':
        return <Clock size={16} className="text-amber-500" />;
      case 'cancelled':
        return <XCircle size={16} className="text-slate-400" />;
      default:
        return <Clock size={16} className="text-slate-400" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'processing':
        return 'Processing...';
      case 'pending':
        return 'Queued';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-50 dark:bg-green-900/20';
      case 'failed':
        return 'text-red-600 bg-red-50 dark:bg-red-900/20';
      case 'processing':
        return 'text-blue-600 bg-blue-50 dark:bg-blue-900/20';
      case 'pending':
        return 'text-amber-600 bg-amber-50 dark:bg-amber-900/20';
      case 'cancelled':
        return 'text-slate-500 bg-slate-50 dark:bg-slate-900/20';
      default:
        return 'text-slate-500 bg-slate-50';
    }
  };

  const getTypeIcon = (type: string) => {
    return type === 'import' ? (
      <Upload size={14} className="text-purple-500" />
    ) : (
      <Download size={14} className="text-green-500" />
    );
  };


  return (
    <Dropdown className="relative flex items-center h-header">
      <Dropdown.Trigger
        type="button"
        onOpen={handleDropdownOpen}
        className="relative inline-flex justify-center items-center p-0 text-topbar-item transition-all size-[37.5px] duration-75 ease-linear bg-topbar rounded-md btn hover:bg-topbar-item-bg-hover hover:text-topbar-item-hover group-data-[topbar=dark]:bg-topbar-dark group-data-[topbar=dark]:text-topbar-item-dark group-data-[topbar=dark]:hover:bg-topbar-item-bg-hover-dark group-data-[topbar=dark]:hover:text-topbar-item-hover-dark group-data-[topbar=brand]:bg-topbar-brand group-data-[topbar=brand]:text-topbar-item-brand group-data-[topbar=brand]:hover:bg-topbar-item-bg-hover-brand group-data-[topbar=brand]:hover:text-topbar-item-hover-brand group-data-[topbar=dark]:dark:bg-zink-700 group-data-[topbar=dark]:dark:text-zink-200 group-data-[topbar=dark]:dark:hover:bg-zink-600 group-data-[topbar=dark]:dark:hover:text-zink-50"
      >
        <ClipboardList className="inline-block size-5 stroke-1 fill-slate-100 group-data-[topbar=dark]:fill-topbar-item-bg-hover-dark group-data-[topbar=brand]:fill-topbar-item-bg-hover-brand" />
      </Dropdown.Trigger>

      <Dropdown.Content
        placement="right-end"
        className="absolute z-50 ltr:text-left rtl:text-right bg-white rounded-md shadow-md !top-4 dropdown-menu min-w-[340px] dark:bg-zink-600"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500">
          <div className="flex items-center gap-2">
            <FileSpreadsheet size={18} className="text-custom-500" />
            <h6 className="text-sm font-semibold text-slate-800 dark:text-zink-50">
              Background Jobs
            </h6>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              fetchJobs();
            }}
            disabled={loading}
            className="p-1.5 text-slate-500 hover:text-custom-500 hover:bg-slate-100 dark:hover:bg-zink-500 rounded transition-colors"
            title="Refresh"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* Jobs List */}
        <SimpleBar className="max-h-[350px]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={24} className="text-custom-500 animate-spin" />
            </div>
          ) : !hasFetched ? (
            <div className="py-8 text-center">
              <ClipboardList size={32} className="mx-auto text-slate-300 dark:text-zink-500 mb-2" />
              <p className="text-sm text-slate-500 dark:text-zink-400">
                Loading jobs...
              </p>
            </div>
          ) : jobs.length === 0 ? (
            <div className="py-8 text-center">
              <ClipboardList size={32} className="mx-auto text-slate-300 dark:text-zink-500 mb-2" />
              <p className="text-sm text-slate-500 dark:text-zink-400">
                No background jobs yet
              </p>
              <p className="text-xs text-slate-400 dark:text-zink-500 mt-1">
                Import/Export jobs will appear here
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-zink-500">
              {jobs.map((job) => (
                <li
                  key={job.jobId}
                  className="p-3 hover:bg-slate-50 dark:hover:bg-zink-500/50 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    {/* Type Icon */}
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="p-2 bg-slate-100 dark:bg-zink-500 rounded-lg">
                        {getTypeIcon(job.type)}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-slate-800 dark:text-zink-100 truncate">
                          {job.jobName}
                        </p>
                        <span className="text-xs text-slate-400 dark:text-zink-400 whitespace-nowrap">
                          {formatDate(job.createdAt)}
                        </span>
                      </div>

                      {/* Status Badge */}
                      <div className="flex items-center gap-2 mt-1.5">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(
                            job.status
                          )}`}
                        >
                          {getStatusIcon(job.status)}
                          {getStatusLabel(job.status)}
                        </span>

                        {/* Progress Info */}
                        {job.status === 'completed' && job.totalRows !== undefined && (
                          <span className="text-xs text-slate-500 dark:text-zink-400">
                            {job.successCount || 0}/{job.totalRows} rows
                          </span>
                        )}
                      </div>

                      {/* Job ID */}
                      <p className="text-xs text-slate-400 dark:text-white mt-1 font-mono truncate">
                        {job.jobId}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SimpleBar>

        {/* Footer */}
        {jobs.length > 0 && (
          <div className="p-3 border-t border-slate-200 dark:border-zink-500">
            <p className="text-xs text-slate-500 dark:text-zink-400 text-center">
              Results are sent to your email
            </p>
          </div>
        )}
      </Dropdown.Content>
    </Dropdown>
  );
};

export default BackgroundJobsDropdown;
